import {
  faArrowDownToLine,
  faCircleExclamation,
  faEye,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ActionIcon,
  Box,
  Group,
  Progress,
  Text,
  ThemeIcon,
} from '../../../core';
import { canPreviewArtifact } from '../../artifact-preview-kind';
import { getArtifactFileMeta } from '../../artifact-file-meta';
import { useDownloadFeedback } from '../../use-download-feedback';

export type ArtifactRowStatus = 'ready' | 'generating' | 'failed';

export interface ArtifactRowProps {
  readonly filename: string;
  readonly downloadUrl: string;
  readonly sizeLabel?: string;
  /** Type-specific detail, e.g. `148 rows · 6 columns`. Computed backend-side. */
  readonly summary?: string;
  readonly status?: ArtifactRowStatus;
  /** 0-100. Only meaningful while `status === 'generating'`. */
  readonly progress?: number;
  readonly error?: string;
  readonly onPreview?: () => void | Promise<void>;
  readonly onDownload?: () => void | Promise<void>;
}

// Sizes go through `style` in raw px rather than Mantine's spacing props,
// because those convert numbers to `rem` against `--mantine-scale` — so
// `py={10}` is only 10px when the root font size is exactly 16px, and silently
// inflates otherwise. Raw px makes the row height the same everywhere.
//
// The row reads as one unit when the text block matches the tile:
// 18px (filename) + 16px (meta) === 34px ≈ the 34px tile.
const TILE_PX = 34;
const ICON_PX = 17;
const FILENAME_LINE_PX = 18;
const META_LINE_PX = 16;
const ACTION_PX = 28;
const ROW_PAD_Y_PX = 6;
const ROW_PAD_X_PX = 12;

/** Subtitle text for the row, by status. Split out rather than expressed as a
 *  chained ternary — stacked ternaries are banned by the house style. */
function buildDetail(
  status: ArtifactRowStatus,
  typeName: string,
  sizeLabel?: string,
  summary?: string,
  error?: string
): string {
  if (status === 'failed') return error || 'Generation failed';
  if (status === 'generating') return 'Generating…';
  return [typeName, sizeLabel, summary].filter(Boolean).join(' · ');
}

export function ArtifactRow({
  filename,
  downloadUrl,
  sizeLabel,
  summary,
  status = 'ready',
  progress,
  error,
  onPreview,
  onDownload,
}: ArtifactRowProps) {
  const { isDownloading, startDownload } = useDownloadFeedback(
    downloadUrl,
    onDownload
  );
  const fileType = getArtifactFileMeta(filename);
  const isReady = status === 'ready';
  const isFailed = status === 'failed';

  // The eye icon is hidden — not disabled — for formats with no viable inline
  // rendering. A disabled control still advertises a capability that doesn't
  // exist for this file.
  const showPreview = isReady && canPreviewArtifact(filename);
  const detail = buildDetail(
    status,
    fileType.typeName,
    sizeLabel,
    summary,
    error
  );
  const tint = isFailed ? 'red' : fileType.color;

  return (
    <Group
      wrap="nowrap"
      align="center"
      style={{
        gap: ROW_PAD_X_PX,
        padding: `${ROW_PAD_Y_PX}px ${ROW_PAD_X_PX}px`,
      }}
    >
      {/* ThemeIcon rather than a hand-rolled tile: `variant="light"` resolves to
          the scheme-aware --mantine-color-{c}-light pair, so the tile darkens in
          dark mode instead of staying a bright pastel wash. Sized in raw px for
          the same reason as everything else here. */}
      <ThemeIcon
        aria-hidden
        variant="light"
        color={tint}
        style={{
          flexShrink: 0,
          width: TILE_PX,
          height: TILE_PX,
          minWidth: TILE_PX,
          borderRadius: 8,
        }}
      >
        <FontAwesomeIcon
          icon={isFailed ? faCircleExclamation : fileType.icon}
          style={{ fontSize: ICON_PX }}
        />
      </ThemeIcon>

      <Box style={{ flex: 1, minWidth: 0 }}>
        <Text
          fw={500}
          truncate
          style={{ fontSize: 13, lineHeight: `${FILENAME_LINE_PX}px` }}
        >
          {filename}
        </Text>
        <Text
          c={isFailed ? 'red.7' : 'dimmed'}
          truncate
          style={{ fontSize: 11, lineHeight: `${META_LINE_PX}px` }}
        >
          {detail}
        </Text>
        {status === 'generating' && (
          <Progress
            size={4}
            style={{ marginTop: 4 }}
            radius="xl"
            value={progress ?? 0}
            aria-label={`Generating ${filename}`}
          />
        )}
      </Box>

      {/* The two icon buttons sit 2px apart as a single control cluster, not on
          the row's 12px rhythm — at 12px they read as two unrelated controls. */}
      <Group wrap="nowrap" style={{ flexShrink: 0, gap: 2 }}>
        {showPreview && (
          <ActionIcon
            variant="subtle"
            color="gray"
            style={{
              width: ACTION_PX,
              height: ACTION_PX,
              minWidth: ACTION_PX,
              borderRadius: 6,
            }}
            aria-label={`Preview ${filename}`}
            onClick={() => {
              if (!onPreview) return;
              Promise.resolve(onPreview()).catch(() => undefined);
            }}
          >
            <FontAwesomeIcon icon={faEye} style={{ fontSize: 16 }} />
          </ActionIcon>
        )}

        {isReady && (
          <ActionIcon
            variant="subtle"
            color="gray"
            style={{
              width: ACTION_PX,
              height: ACTION_PX,
              minWidth: ACTION_PX,
              borderRadius: 6,
            }}
            loading={isDownloading}
            aria-label={`Download ${filename}`}
            onClick={startDownload}
          >
            <FontAwesomeIcon
              icon={faArrowDownToLine}
              style={{ fontSize: 16 }}
            />
          </ActionIcon>
        )}
      </Group>
    </Group>
  );
}
